const REPLICA_MESSAGE_ID_PREFIX = "replica-source-v1:";

export function isReservedReplicaSourceMessageId(value: string): boolean {
  return value.startsWith(REPLICA_MESSAGE_ID_PREFIX);
}

/**
 * A hydrated message keeps its CMMD turn inside its id rather than in the
 * projection's `turnId`: `retainThreadMessagesAfterRevert` drops messages whose
 * turn is not among the local checkpoints, and a hydrated thread has none, so
 * a later local revert would erase recovered history. The id is the one slot
 * the serializer can read back without touching revert semantics.
 */
export function encodeReplicaSourceMessageId(input: {
  readonly sequence: number;
  readonly scope: string;
  readonly eventId: string;
  readonly turnId?: string | null;
}): string {
  const base = `${REPLICA_MESSAGE_ID_PREFIX}${input.sequence}:${encodeSegment(input.scope)}:${encodeSegment(input.eventId)}`;
  return input.turnId ? `${base}:${encodeSegment(input.turnId)}` : base;
}

/** Decodes one base64url segment, or null when it does not round-trip exactly. */
function decodeExactSegment(encoded: string): string | null {
  try {
    const bytes = Uint8Array.from(atob(encoded.replaceAll("-", "+").replaceAll("_", "/")), (char) =>
      char.charCodeAt(0),
    );
    const decoded = new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(bytes);
    return decoded.length > 0 && encodeSegment(decoded) === encoded ? decoded : null;
  } catch {
    return null;
  }
}

export function decodeReplicaSourceMessageId(value: string): {
  readonly sequence: number;
  readonly eventId: string;
  readonly turnId?: string;
} | null {
  if (!value.startsWith(REPLICA_MESSAGE_ID_PREFIX)) return null;
  const [sequenceText, encodedScope, encodedEventId, encodedTurnId, ...rest] = value
    .slice(REPLICA_MESSAGE_ID_PREFIX.length)
    .split(":");
  if (rest.length > 0 || encodedScope === undefined || encodedEventId === undefined) return null;
  const sequence = Number(sequenceText);
  if (!Number.isSafeInteger(sequence) || sequence <= 0) return null;
  const scope = decodeExactSegment(encodedScope);
  const eventId = decodeExactSegment(encodedEventId);
  if (scope === null || eventId === null) return null;
  if (encodedTurnId === undefined) return { sequence, eventId };
  const turnId = decodeExactSegment(encodedTurnId);
  return turnId === null ? null : { sequence, eventId, turnId };
}

function encodeSegment(value: string): string {
  return btoa(
    Array.from(new TextEncoder().encode(value), (byte) => String.fromCharCode(byte)).join(""),
  )
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");
}

export function runtimeMessageSourceEventId(messageId: string): string {
  return decodeReplicaSourceMessageId(messageId)?.eventId ?? `message:${messageId}`;
}
